/* eslint-disable warp-drive/no-legacy-request-patterns */
import moment from 'moment';

// The person a progression belongs to. Progressions from the bulk subject
// query carry person_id; store records expose it as a relationship.
function personIdOf(progression) {
  return (
    progression.person_id ||
    (progression.belongsTo &&
      progression.belongsTo('personId') &&
      progression.belongsTo('personId').id())
  );
}

function normaliseName(name) {
  return (name || '').trim().toLowerCase();
}

/**
 * Progressions from a subject-wide query, grouped by student, across every
 * milestone and year. One pass over the list, however many students.
 *
 * @param {Array} progressions milestones/progression records
 * @return {Object} studentId → that student's progressions
 */
export function progressionsByStudent(progressions) {
  let byStudent = {};
  (progressions || []).forEach((p) => {
    let personId = personIdOf(p);
    if (!personId) return;
    (byStudent[personId] = byStudent[personId] || []).push(p);
  });
  return byStudent;
}

/**
 * The most recent progression for the overall judgement (key 'overall') and
 * for each of the current rubric's strands (keyed by element id), from any
 * milestone or year.
 *
 * A strand recorded against an earlier rubric (another milestone or rubric
 * family) has a different element id, so it is matched to the current
 * rubric's strand with the same name; strands with no match are left out.
 *
 * @param {Array} progressions one student's milestones/progression records
 * @param {Array} rubricElements the current rubric's reports/rubric-element records
 * @param {Object} store to look up earlier rubrics' elements by id
 * @return {Object} key → progression
 */
export function latestProgressionMap(progressions, rubricElements, store) {
  let currentIds = new Set();
  let idByName = {};
  (rubricElements || []).forEach((element) => {
    let id = element.get('id');
    currentIds.add(id);
    let name = normaliseName(element.get('name'));
    if (name && !idByName[name]) idByName[name] = id;
  });

  // Earlier rubrics' element ids resolve to the same key every time, so look
  // each one up once
  let keyCache = {};
  let keyFor = (elementId) => {
    if (!elementId) return 'overall';
    if (currentIds.has(elementId)) return elementId;
    if (!(elementId in keyCache)) {
      let earlier =
        store && store.peekRecord('reports/rubric-element', elementId);
      keyCache[elementId] =
        (earlier && idByName[normaliseName(earlier.get('name'))]) || null;
    }
    return keyCache[elementId];
  };

  // Keep the newest per key in one pass rather than sorting the history
  let map = {};
  (progressions || []).forEach((progression) => {
    let key = keyFor(progression.get('elementId'));
    if (!key) return;
    let kept = map[key];
    if (
      !kept ||
      (progression.get('createTime') || 0) > (kept.get('createTime') || 0)
    ) {
      map[key] = progression;
    }
  });
  return map;
}

/**
 * Where a pre-filled judgement came from, for the "Previously entered"
 * badge: the milestone it was recorded against and the date, e.g.
 * "Year 4 mid-year, 14 Jun 2025".
 *
 * @param {Object} progression a milestones/progression record
 * @param {Object} store to look up its milestone
 * @return {string}
 */
export function progressionSource(progression, store) {
  if (!progression) return '';
  let createTime = progression.get('createTime');
  let date = createTime ? moment.unix(createTime).format('D MMM YYYY') : '';
  let milestoneId =
    progression.belongsTo &&
    progression.belongsTo('milestoneId') &&
    progression.belongsTo('milestoneId').id();
  let milestone =
    milestoneId &&
    store &&
    store.peekRecord('milestones/milestone', milestoneId);
  let label = milestone && milestone.get('label');
  return [label, date].filter(Boolean).join(', ');
}
